// Simple Chess Platform API Service

export const fetchChessData = async (platform, username) => {
  if (!username) return null;

  try {
    if (platform === 'chessCom') {
      // Chess.com API
      const response = await fetch(`https://api.chess.com/pub/player/${username}/stats`);
      if (!response.ok) throw new Error('Player not found');
      
      const data = await response.json();
      
      // Get highest rating from available time controls
      let rating = 0;
      let ratingType = null;
      let gameCount = 0;
      
      if (data.chess_rapid?.last?.rating) {
        rating = data.chess_rapid.last.rating;
        ratingType = 'Rapid';
      } else if (data.chess_blitz?.last?.rating) {
        rating = data.chess_blitz.last.rating;
        ratingType = 'Blitz';
      } else if (data.chess_bullet?.last?.rating) {
        rating = data.chess_bullet.last.rating;
        ratingType = 'Bullet';
      }
      
      // Count total games
      const timeControls = ['chess_rapid', 'chess_blitz', 'chess_bullet'];
      timeControls.forEach(tc => {
        if (data[tc]?.record) {
          gameCount += (data[tc].record.win || 0) + (data[tc].record.loss || 0) + (data[tc].record.draw || 0);
        }
      });

      return {
        rating,
        ratingType,
        online: false, // Chess.com doesn't provide this in stats endpoint
        gameCount
      };
    } else if (platform === 'lichess') {
      // Try to get more data from Lichess
      const response = await fetch(`https://lichess.org/api/user/${username}`);
      if (!response.ok) throw new Error('Player not found');
      
      const data = await response.json();
      
      // Get highest rating from perfs
      let rating = 0;
      let ratingType = null;
      if (data.perfs?.rapid?.rating) {
        rating = data.perfs.rapid.rating;
        ratingType = 'Rapid';
      } else if (data.perfs?.blitz?.rating) {
        rating = data.perfs.blitz.rating;
        ratingType = 'Blitz';
      } else if (data.perfs?.bullet?.rating) {
        rating = data.perfs.bullet.rating;
        ratingType = 'Bullet';
      } else if (data.perfs?.classical?.rating) {
        rating = data.perfs.classical.rating;
        ratingType = 'Classical';
      }
      
      // Get total games played
      let gameCount = 0;
      if (data.count?.all) gameCount = data.count.all;
      
      return {
        rating,
        ratingType,
        online: data.online || false,
        gameCount
      };
    }
  } catch (error) {
    console.error('Chess API Error:', error);
    return null;
  }
};

// Fetch recent wins from Lichess
export const fetchLichessWins = async (username, maxGames = 10) => {
  try {
    const response = await fetch(
      `https://lichess.org/api/games/user/${username}?max=${maxGames * 2}&rated=true&perfType=blitz,rapid,bullet`,
      {
        headers: {
          'Accept': 'application/x-ndjson'
        }
      }
    );

    if (!response.ok) throw new Error('Player not found');

    const text = await response.text();
    const games = text
      .split('\n')
      .filter(line => line.trim())
      .map(line => JSON.parse(line))
      .filter(game => {
        // Only include games where the user won
        const isWhite = game.players.white.user?.id?.toLowerCase() === username.toLowerCase();
        const winner = game.winner;
        return (isWhite && winner === 'white') || (!isWhite && winner === 'black');
      })
      .slice(0, maxGames)
      .map(game => {
        const isWhite = game.players.white.user?.id?.toLowerCase() === username.toLowerCase();
        const opponent = isWhite ? game.players.black.user?.name : game.players.white.user?.name;
        const userColor = isWhite ? 'white' : 'black';

        return {
          id: game.id,
          url: `https://lichess.org/${game.id}`,
          opponent: opponent || 'Anonymous',
          result: 'Won',
          userColor,
          date: new Date(game.createdAt).toLocaleDateString(),
          timeControl: game.speed
        };
      });

    return games;
  } catch (error) {
    console.error('Lichess API Error:', error);
    throw error;
  }
};

// Fetch recent wins from Chess.com
export const fetchChessComWins = async (username, maxGames = 10) => {
  try {
    // First, get list of archives
    const archivesResponse = await fetch(`https://api.chess.com/pub/player/${username}/games/archives`);
    if (!archivesResponse.ok) throw new Error('Player not found');

    const archivesData = await archivesResponse.json();
    const archives = archivesData.archives || [];

    if (archives.length === 0) {
      return [];
    }

    // Get the most recent archives (last 2 months)
    const recentArchives = archives.slice(-2);
    const allGames = [];

    for (const archiveUrl of recentArchives.reverse()) {
      const gamesResponse = await fetch(archiveUrl);
      if (gamesResponse.ok) {
        const gamesData = await gamesResponse.json();
        allGames.push(...(gamesData.games || []));
      }

      // Stop if we have enough games
      if (allGames.length >= maxGames * 3) break;
    }

    // Filter for wins and format
    const wins = allGames
      .filter(game => {
        const usernameLower = username.toLowerCase();
        const isWhite = game.white.username.toLowerCase() === usernameLower;
        const isBlack = game.black.username.toLowerCase() === usernameLower;

        if (isWhite && game.white.result === 'win') return true;
        if (isBlack && game.black.result === 'win') return true;
        return false;
      })
      .slice(0, maxGames)
      .map(game => {
        const usernameLower = username.toLowerCase();
        const isWhite = game.white.username.toLowerCase() === usernameLower;
        const opponent = isWhite ? game.black.username : game.white.username;
        const userColor = isWhite ? 'white' : 'black';

        return {
          id: game.uuid || game.url,
          url: game.url,
          opponent,
          result: 'Won',
          userColor,
          date: new Date(game.end_time * 1000).toLocaleDateString(),
          timeControl: game.time_class
        };
      });

    return wins;
  } catch (error) {
    console.error('Chess.com API Error:', error);
    throw error;
  }
};